import { useEffect, useState } from "react";
import { ArrowLeft, ArrowRight, Check } from "react-feather";
import { useToasts } from "react-toast-notifications";
import {
  tenantInfoSchema,
  storeSetupSchema,
} from "../../components/TenantSchema/tenantSchema";

import { apiHandler } from "../../api";
import { endpoint } from "../../api/endpoint";
import TenantInfoStep from "./TenantInfoStep";
import StoreSetupStep from "./StoreSetupText";
import { useNavigate } from "react-router";

const steps = [
  { id: "tenant-info", title: "Merchant Information" },
  { id: "store-setup", title: "Store Setup" },
];

const FormProgress = ({ steps, currentStepIndex }) => (
  <div className="p-6 border-b border-gray-100">
    <div className="flex items-center justify-between mb-2">
      <h2 className="text-xl font-semibold text-gray-800">
        {steps[currentStepIndex].title}
      </h2>
      <div className="text-sm text-gray-500">
        Step {currentStepIndex + 1} of {steps.length}
      </div>
    </div>
    <div className="w-full bg-gray-200 rounded-full h-2">
      <div
        className="bg-blue-500 h-2 rounded-full transition-all duration-300 ease-in-out"
        style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
      ></div>
    </div>
  </div>
);

const FormNavigation = ({
  currentStepIndex,
  steps,
  goToPreviousStep,
  isNextDisabled,
  isSubmitting,
}) => (
  <div className="px-8 py-4 bg-gray-50 flex justify-between items-center">
    <button
      type="button"
      onClick={goToPreviousStep}
      disabled={currentStepIndex === 0 || isSubmitting}
      className="flex items-center px-6 py-2 rounded-md transition-all duration-200 disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed bg-white text-gray-700 border border-gray-200 hover:bg-gray-100 hover:border-gray-300"
    >
      <ArrowLeft size={16} className="mr-2" /> Back
    </button>

    {currentStepIndex === steps.length - 1 ? (
      <button
        type="submit"
        disabled={isSubmitting}
        className="flex items-center px-6 py-2 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all duration-200 shadow-sm font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        {isSubmitting ? (
          <>
            <svg className="animate-spin h-4 w-4 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
          </>
        ) : (
          <>
            Complete <Check size={16} className="ml-2" />
          </>
        )}
      </button>
    ) : (
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById("next-step-btn").click();
        }} // We link this to the form's real next button
        disabled={isNextDisabled || isSubmitting}
        className="flex items-center px-6 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600 transition-all duration-200 shadow-sm font-semibold disabled:bg-gray-400 disabled:cursor-not-allowed"
      >
        Next <ArrowRight size={16} className="ml-2" />
      </button>
    )}
  </div>
);

// Helper function to get the first field with an error
const getFirstErrorField = (errors) => {
  if (!errors || Object.keys(errors).length === 0) return null;

  // Prioritize checklist field if it has errors
  if (errors.checklist) {
    return "checklist";
  }

  for (const [fieldName, fieldError] of Object.entries(errors)) {
    // For nested objects like customer_support or gro_support, return the parent field
    if (typeof fieldError === "object" && fieldError !== null) {
      return fieldName;
    }
    // For simple fields, return the field name
    return fieldName;
  }

  return null;
};

// Helper function to scroll to and focus on a field
const scrollToErrorField = (fieldName) => {
  // Use setTimeout to ensure the DOM has updated with error messages
  setTimeout(() => {
    // Try multiple selectors to find the field
    let element =
      document.getElementById(fieldName) ||
      document.querySelector(`[name="${fieldName}"]`) ||
      document.querySelector(`[data-field="${fieldName}"]`);

    if (element) {
      // Scroll the element into view smoothly
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      // Focus on the element if it's focusable
      if (typeof element.focus === "function") {
        element.focus();
      }
    }
  }, 100);
};

const OnBoardTenant = () => {
  const { addToast } = useToasts();
  const navigate = useNavigate();
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formState, setFormState] = useState({
    // Tenant Info
    userName: "",
    email: "",
    npType: "",
    npDomain: "",
    msnFee: {
      type: "", // e.g., "perorder"
      amount: "", // the numeric value
      valueType: "", // e.g., "percent" or "fixed"
    },
    stages: "",
    nokey: "",
    services: [],
    webhook: "",
    settlement_agency_public_key: "",
    np_subscriber_private_id: "",
    np_unique_id: "",
    np_signing_public_id: "",
    npId: "",
    npURL: "",
    shiprocketEmail: "",
    shiprocketPassword: "",
    retailDomains: [],
    brandName: "",
    sBrandLogo: null,
    hasSellerApp: "",
    withSellerApp: "",
    customer_support: { name: "", email: "", phone: "" },
    gro_support: { name: "", email: "", phone: "" },
  });

  const [options, setOptions] = useState({
    npTypeOptions: [],
    buyerDomainOptions: [],
    sellerDomainOptions: [],
    buyerProductOptions: [],
    retailDomains: [],
    sellerProductOptions: {},
    storeTypes: [],
    env: [],
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [checklistOptions, setChecklistOptions] = useState([]);
  const [checklistSelection, setChecklistSelection] = useState([]);

  useEffect(() => {
    const fetchOnboardOptions = async () => {
      setIsLoading(true);
      try {
        const response = await apiHandler({
          url: endpoint.GET_ONBOARD_OPTIONS,
        });
        if (response.data.success) {
          const responseData = response.data.data;
          setOptions(responseData);

          const defaultKeysMap = (responseData.default_keys || []).reduce(
            (acc, item) => {
              if (item?.key_name) {
                acc[item.key_name] = item.key_value || "";
              }
              return acc;
            },
            {},
          );

          if (responseData.storeTypes.length > 0) {
            const storeTypes = responseData.storeTypes;

            // If seller app is "No", default to Custom
            let defaultStoreTypeId;
            if (formState.withSellerApp === "No") {
              const customStore = storeTypes.find((s) => s.name === "Custom");
              defaultStoreTypeId =
                customStore?.iStoreTypeId || storeTypes[0].iStoreTypeId;
            } else {
              defaultStoreTypeId = storeTypes[0].iStoreTypeId;
            }

            setFormState((prev) => ({
              ...prev,
              storeTypeId: defaultStoreTypeId,
              storeKeys: [],
              webhook:
                prev.webhook ||
                defaultKeysMap.np_communication_webhook ||
                "",
              settlement_agency_public_key:
                prev.settlement_agency_public_key ||
                defaultKeysMap.settlement_agency_public_key ||
                "",
              np_subscriber_private_id:
                prev.np_subscriber_private_id ||
                defaultKeysMap.subscriber_private_id ||
                "",
              np_unique_id:
                prev.np_unique_id || defaultKeysMap.unique_id || "",
              np_signing_public_id:
                prev.np_signing_public_id ||
                defaultKeysMap.signing_public_id ||
                "",
              nokey: prev.nokey || defaultKeysMap.no_keys || "",
            }));
          } else {
            setFormState((prev) => ({
              ...prev,
              webhook:
                prev.webhook ||
                defaultKeysMap.np_communication_webhook ||
                "",
              settlement_agency_public_key:
                prev.settlement_agency_public_key ||
                defaultKeysMap.settlement_agency_public_key ||
                "",
              np_subscriber_private_id:
                prev.np_subscriber_private_id ||
                defaultKeysMap.subscriber_private_id ||
                "",
              np_unique_id:
                prev.np_unique_id || defaultKeysMap.unique_id || "",
              np_signing_public_id:
                prev.np_signing_public_id ||
                defaultKeysMap.signing_public_id ||
                "",
              nokey: prev.nokey || defaultKeysMap.no_keys || "",
            }));
          }
        }
      } catch (error) {
       
      } finally {
        setIsLoading(false);
      }
    };
    fetchOnboardOptions();
  }, []);

  useEffect(() => {
    const fetchChecklistOptions = async () => {
      if (formState.npDomain && formState.stages) {
        setValidationErrors((prevErr) => ({ ...prevErr, checklist: "" }));
        const response = await apiHandler({
          url: `${endpoint.GET_CHECKLIST_OPTIONS}?environment_id=${formState.stages}&type=${formState.npDomain}`,
        });

        if (response.data.success) {
          const checklistData = response.data.data.checklist || [];
          setChecklistOptions(checklistData);

          // INITIALIZE SELECTION STATE HERE
          // Map the options to the format required by your submit API
          setChecklistSelection(
            checklistData.map((opt) => ({
              sKey: opt.key,
              bValue: false, // Defaulting to false for all initially
            })),
          );
        }
      }
    };

    fetchChecklistOptions();
  }, [formState.npDomain, formState.stages]);

  // ✅ SEPARATE EFFECT: Update services and NP IDs based on domain and options
  // This prevents infinite loops by not updating formState within the checklist effect
  useEffect(() => {
    const isRetailMarketConnectMSN = formState.npDomain === "Retail - MSN Provider";

    setFormState((prev) => {
      let updatedServices = [...(prev.services || [])];

      // Only auto-add RSF and IGM for Retail - MSN Provider (MarketConnect)
      if (isRetailMarketConnectMSN) {
        // Add RSF and IGM if they aren't already in the list
        if (!updatedServices.includes("RSF")) updatedServices.push("RSF");
        if (!updatedServices.includes("IGM")) updatedServices.push("IGM");
      }

      return {
        ...prev,
        services: updatedServices,
        npId: isRetailMarketConnectMSN ? options?.marketConnectNPId || prev.npId : prev.npId,
        npURL: isRetailMarketConnectMSN ? options?.marketConnectNPURL || prev.npURL : prev.npURL,
      };
    });
  }, [
    formState.npDomain,
    options?.marketConnectNPId,
    options?.marketConnectNPURL,
  ]);

  // ✅ AUTO-SET STAGES TO PROD WHEN SELLER APP IS "YES"
  useEffect(() => {
    if (formState.hasSellerApp === "Yes" && options.env) {
      // Find the "prod" or "Production" environment
      const prodEnv = options.env.find(
        (env) =>
          env.environment_name?.toLowerCase() === "prod"
      );

      if (prodEnv) {
        setFormState((prev) => ({
          ...prev,
          stages: String(prodEnv.id),
        }));
      }
    }
  }, [formState.hasSellerApp, options.env]);

  const tenantInfoFields = [
    "userName",
    "email",
    "npType",
    "npDomain",
    "msnFee",
    "stages",
    "nokey",
    "services",
    "webhook",
    "settlement_agency_public_key",
    "np_subscriber_private_id",
    "np_unique_id",
    "np_signing_public_id",
    "hasSellerApp",
    "withSellerApp",
    "npId",
    "npURL",
    "retailDomains",
    "shiprocketEmail",
    "shiprocketPassword",
    "customer_support",
    "gro_support",
    "checklist",
  ];

  const storeSetupFields = [
    "brandName",
    "sBrandLogo",
    "storeKeys",
    "storeTypeId",
  ];

  const getStepData = (stepIndex, data, checklistData = []) => {
    const keys = stepIndex === 0 ? tenantInfoFields : storeSetupFields;
    const filtered = {};
    keys.forEach((key) => {
      if (key === "checklist") {
        // Convert checklistSelection array to checklist object
        // checklistData is passed as parameter, falling back to empty array
        const checklistObj = checklistData.reduce((acc, item) => {
          acc[item.sKey] = item.bValue;
          return acc;
        }, {});
        filtered[key] = checklistObj;
      } else {
        filtered[key] = data[key];
      }
    });
    return filtered;
  };

  const validateCurrentStep = () => {
    const currentSchema =
      currentStepIndex === 0 ? tenantInfoSchema : storeSetupSchema;

    const stepData = getStepData(currentStepIndex, formState, checklistSelection);

    const { error } = currentSchema.validate(stepData, { abortEarly: false });

    let validationErrors = {};

    if (error) {
      error.details.forEach((detail) => {
        const path = detail.path; // e.g., ['customer_support', 'phone'] or ['userName']

        if (path[0] === "storeKeys" && typeof path[1] === "number") {
          // Handle special storeKeys array logic
          const keyName = formState.storeKeys?.[path[1]]?.key;
          if (keyName) {
            if (!validationErrors.storeKeys) validationErrors.storeKeys = {};
            validationErrors.storeKeys[keyName] = detail.message;
          }
        } else if (path.length === 2) {
          // Handle nested objects like customer_support
          const [outer, inner] = path; // ['customer_support', 'phone']
          if (!validationErrors[outer]) {
            validationErrors[outer] = {};
          }
          validationErrors[outer][inner] = detail.message;
        } else if (path.length === 1) {
          // Handle top-level fields
          validationErrors[path[0]] = detail.message; // ['userName']
        }
      });
    }

    // ✅ ADDITIONAL VALIDATION: Check if checklist options are present and at least one must be selected
    if (currentStepIndex === 0 && checklistOptions.length > 0) {
      const hasSelectedChecklist = checklistSelection.some(
        (item) => item.bValue === true
      );

      if (!hasSelectedChecklist) {
        validationErrors.checklist =
          "At least one checklist option must be selected";
      }
    }

    // ✅ ADDITIONAL VALIDATION: Check if products must be selected
    // If "Does Merchant have a Seller App live on ONDC?" = "NO"
    // AND "Include Seller App in onboarding?" = "NO"
    // Then at least one product must be selected
    if (
      currentStepIndex === 0 &&
      formState.hasSellerApp === "No" &&
      formState.withSellerApp === "No" &&
      (!formState.services || formState.services.length === 0)
    ) {
      validationErrors.services =
        "At least one product must be selected when Seller App is not included";
    }

    // ✅ ADDITIONAL VALIDATION: When NOT on staging, require NO Keys, Subscriber ID, and Subscriber URL
    if (currentStepIndex === 0 && formState.stages) {
      // Find the selected environment to check if it's "staging"
      const selectedEnv = options.env?.find(
        (env) => String(env.id) === String(formState.stages)
      );
      const isStaging =
        selectedEnv?.environment_name?.toLowerCase().includes("staging");

      // Only require these fields if NOT on staging
      if (!isStaging) {
        // Check NO Keys
        if (
          (formState.npDomain === "Retail - MSN Provider" ||
            formState.npDomain === "Retail - ISN" ||
            formState.npDomain === "Retail - MSN") &&
          !formState.nokey
        ) {
          validationErrors.nokey = "NO Keys is required";
        }

        const hasShiprocketCredentials =
          !!formState.shiprocketEmail && !!formState.shiprocketPassword;
        const hasNimbusCredentials =
          !!formState.nimbusEmail && !!formState.nimbusPassword;

        if (!hasShiprocketCredentials && !hasNimbusCredentials) {
          if (!formState.shiprocketEmail) {
            validationErrors.shiprocketEmail = "Shiprocket Email is required";
          }

          if (!formState.shiprocketPassword) {
            validationErrors.shiprocketPassword =
              "Shiprocket Password is required";
          }

          if (!formState.nimbusEmail) {
            validationErrors.nimbusEmail = "Nimbus Email is required";
          }

          if (!formState.nimbusPassword) {
            validationErrors.nimbusPassword = "Nimbus Password is required";
          }
        }
      }
    }

    return Object.keys(validationErrors).length > 0 ? validationErrors : null;
  };

  const goToNextStep = () => {
    // Clear any previous API errors when user attempts to proceed
    if (validationErrors?.apiError) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated.apiError;
        return updated;
      });
    }
    
    const errors = validateCurrentStep();

    if (errors) {
      setValidationErrors(errors); // ✅ Show field-level errors only
      
      // Scroll to the first field with an error
      const firstErrorField = getFirstErrorField(errors);
      if (firstErrorField) {
        scrollToErrorField(firstErrorField);
      }
      return;
    }

    setValidationErrors({}); // ✅ Clear any old errors
    if (currentStepIndex < steps.length - 1) {
      setCurrentStepIndex((prev) => prev + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPreviousStep = () => {
    // Clear any previous API errors when navigating
    if (validationErrors?.apiError) {
      setValidationErrors((prev) => {
        const updated = { ...prev };
        delete updated.apiError;
        return updated;
      });
    }
    
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const errors = validateCurrentStep();

    if (errors) {
      setValidationErrors(errors);
      
      // Scroll to the first field with an error
      const firstErrorField = getFirstErrorField(errors);
      if (firstErrorField) {
        scrollToErrorField(firstErrorField);
      }
      return;
    }

    setValidationErrors({});
    setIsSubmitting(true);

    try {
      // ✅ Prepare and send the FormData
      const submissionData = new FormData();
    Object.entries(formState).forEach(([key, value]) => {
      if (value == null || key === "logo") {
        return;
      }

      if (key === "sBrandLogo" && value instanceof File) {
        submissionData.append(key, value, value.name);
      } else if (key === "retailDomains") {
        const domainString = Array.isArray(value)
          ? value.join(",")
          : value || "";
        submissionData.append(key, domainString);
      } else if (key === "msnFee") {
        const msnJsonValue = JSON.stringify({
          type: value.type,
          amount: value.amount,
          valueType: value.valueType,
        });
        submissionData.append("msnFee", msnJsonValue);
      } else if (key === "stages") {
        const stagesValue = {
          currentStage: parseInt(value),
          checklist: checklistSelection.reduce((acc, item) => {
            // If you want ALL keys, just assign directly
            acc[item.sKey] = item.bValue; // force all to true
            return acc;
          }, {}),
        };
        submissionData.append(key, JSON.stringify(stagesValue));
      } else {
        const appendValue =
          typeof value === "object" ? JSON.stringify(value) : String(value);
        submissionData.append(key, appendValue);
      }
    });

      // Debug: Show FormData contents
      for (let [key, val] of submissionData.entries()) {
      }

      const response = await apiHandler({
        url: endpoint.CREATE_ONBOARD_TENANT,
        method: "POST",
        data: submissionData,
      });
      
      if (response.data.success) {
        addToast(response.data.message, {
          appearance: "success",
          autoDismiss: true,
        });
        navigate("/merchants");
        return;
      } else {
        // Handle API error response
        const errorMessage = response.data.message || "Error creating merchant";
        addToast(errorMessage, {
          appearance: "error",
          autoDismiss: true,
        });
        
        // Map common API error messages to form fields
        const errorFieldMap = {
          "email already exists": "email",
          "Email already exists": "email",
          "invalid email": "email",
          "user name already exists": "userName",
          "User name already exists": "userName",
        };
        
        // Find matching field from error message
        let errorField = null;
        for (const [key, field] of Object.entries(errorFieldMap)) {
          if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
            errorField = field;
            break;
          }
        }
        
        // If we found a field, set error and scroll to it
        if (errorField) {
          setValidationErrors({ [errorField]: errorMessage });
          scrollToErrorField(errorField);
          
          // Scroll back to step 0 (Tenant Info) where email field is
          if (currentStepIndex !== 0) {
            setCurrentStepIndex(0);
            setTimeout(() => {
              scrollToErrorField(errorField);
            }, 300);
          }
        } else {
          // Generic error without specific field
          setValidationErrors({ apiError: errorMessage });
        }
      }
    } catch (err) {
      const errorMessage = err?.response?.data?.message || err?.message || "Error creating merchant";
      addToast(errorMessage, {
        appearance: "error",
        autoDismiss: true,
      });
      
      // Map potential error messages
      const errorFieldMap = {
        "email already exists": "email",
        "Email already exists": "email",
        "invalid email": "email",
        "user name already exists": "userName",
        "User name already exists": "userName",
      };
      
      let errorField = null;
      for (const [key, field] of Object.entries(errorFieldMap)) {
        if (errorMessage.toLowerCase().includes(key.toLowerCase())) {
          errorField = field;
          break;
        }
      }
      
      if (errorField) {
        setValidationErrors({ [errorField]: errorMessage });
        scrollToErrorField(errorField);
        
        // Scroll back to step 0 (Tenant Info) where email field is
        if (currentStepIndex !== 0) {
          setCurrentStepIndex(0);
          setTimeout(() => {
            scrollToErrorField(errorField);
          }, 300);
        }
      } else {
        setValidationErrors({ apiError: errorMessage });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBlur = (fieldName) => {
    const currentSchema =
      currentStepIndex === 0 ? tenantInfoSchema : storeSetupSchema;

    const stepData = getStepData(currentStepIndex, formState, checklistSelection);

    const { error } = currentSchema.validate(stepData, { abortEarly: false });

    if (!error) {
      setValidationErrors((prev) => {
        if (currentStepIndex === 1 && prev.storeKeys?.[fieldName]) {
          const updatedStoreKeys = { ...prev.storeKeys };
          delete updatedStoreKeys[fieldName];

          return {
            ...prev,
            storeKeys: Object.keys(updatedStoreKeys).length
              ? updatedStoreKeys
              : undefined,
          };
        }
        const newErrors = { ...prev };
        if (
          fieldName === "customer_support" ||
          fieldName === "gro_support" ||
          fieldName === "storeKeys" ||
          fieldName === "checklist"
        ) {
          newErrors[fieldName] = null;
        } else {
          newErrors[fieldName] = null;
        }
        return newErrors;
        // --- END: MODIFICATION ---
      });
      return;
    }

    // ✅ ADDITIONAL VALIDATION: Check checklist field
    if (fieldName === "checklist") {
      if (currentStepIndex === 0 && checklistOptions.length > 0) {
        const hasSelectedChecklist = checklistSelection.some(
          (item) => item.bValue === true
        );

        if (!hasSelectedChecklist) {
          setValidationErrors((prev) => ({
            ...prev,
            checklist: "At least one checklist option must be selected",
          }));
        } else {
          setValidationErrors((prev) => ({
            ...prev,
            checklist: null,
          }));
        }
      }
      return;
    }

    if (fieldName === "customer_support" || fieldName === "gro_support") {
      const nestedErrors = {};
      const relevantErrors = error.details.filter(
        (detail) => detail.path[0] === fieldName,
      );

      if (relevantErrors.length > 0) {
        relevantErrors.forEach((detail) => {
          const [, inner] = detail.path;
          if (inner) {
            nestedErrors[inner] = detail.message;
          }
        });
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: nestedErrors,
        }));
      } else {
        // No errors found for this object, clear it
        setValidationErrors((prev) => ({
          ...prev,
          [fieldName]: null,
        }));
      }
      return; // We're done
    }

    const matchingStoreKeyError = error.details.find((detail) => {
      const [outer, index, inner] = detail.path;
      return (
        outer === "storeKeys" &&
        typeof index === "number" &&
        inner === "value" &&
        formState.storeKeys?.[index]?.key === fieldName
      );
    });

    if (matchingStoreKeyError) {
      setValidationErrors((prev) => ({
        ...prev,
        storeKeys: {
          ...(prev.storeKeys || {}),
          [fieldName]: matchingStoreKeyError.message,
        },
      }));
      return;
    }

    if (fieldName === "retailDomains") {
      const sellerDomainsError = error.details.find(
        (detail) => detail.path[0] === "retailDomains",
      );

      setValidationErrors((prev) => ({
        ...prev,
        retailDomains: sellerDomainsError ? sellerDomainsError.message : null,
      }));
      return;
    }

    const simpleFieldError = error.details.find(
      (detail) => detail.path[0] === fieldName,
    );

    setValidationErrors((prev) => ({
      ...prev,
      [fieldName]: simpleFieldError ? simpleFieldError.message : null,
    }));
  };

  const renderStepComponent = () => {
    if (isLoading) {
      return <div className="text-center p-10">Loading configuration...</div>;
    }
    switch (currentStepIndex) {
      case 0:
        return (
          <TenantInfoStep
            form={formState}
            setForm={setFormState}
            options={options}
            errors={validationErrors}
            onBlur={handleBlur}
            checklistOptions={checklistOptions}
            checklistSelection={checklistSelection}
            setChecklistSelection={setChecklistSelection}
            setValidationErrors={setValidationErrors}
          />
        );
      case 1:
        return (
          <StoreSetupStep
            form={formState}
            setForm={setFormState}
            options={options}
            errors={validationErrors}
            onBlur={handleBlur}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      <div className="max-w-screen-xl mx-auto px-4 py-12">
        <div className="mb-8 text-center">
          <h1
            className="text-3xl font-bold text-gray-800 mb-2"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Merchant Onboarding
          </h1>
          <p className="text-gray-600">
            Complete the steps below to onboard merchant
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <FormProgress steps={steps} currentStepIndex={currentStepIndex} />

          {/* API Error Alert */}
          {validationErrors?.apiError && (
            <div className="mx-6 sm:mx-8 mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">
                    Error Onboarding Merchant
                  </h3>
                  <div className="mt-2 text-sm text-red-700">
                    {validationErrors.apiError}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="px-6 sm:px-8 py-6">{renderStepComponent()}</div>

          <button
            type="button"
            id="next-step-btn"
            onClick={goToNextStep}
            className="hidden"
          ></button>

          {/* The navigation is part of the form to allow the final button to be type="submit" */}
          <FormNavigation
            currentStepIndex={currentStepIndex}
            steps={steps}
            goToPreviousStep={goToPreviousStep}
            isNextDisabled={false} // Add validation logic here if needed
            isSubmitting={isSubmitting}
          />
        </form>

        {/* Full Page Loader Overlay */}
        {isSubmitting && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 flex flex-col items-cente  r gap-4 shadow-xl">
              <svg className="animate-spin h-12 w-12 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <p className="text-gray-700 font-semibold">Creating merchant...</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnBoardTenant;
